import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

const CITIES = [
  {
    name: 'Miami Singles',
    text: 'Miami brings together different cultures, personalities, and ways of life. Find someone whose pace and outlook feel naturally in sync with yours.',
  },
  {
    name: 'Orlando Singles',
    text: 'Orlando singles build lives around careers, interests, and their own routines. Meet someone whose lifestyle and goals align with where you are right now.',
  },
  {
    name: 'Tampa Singles',
    text: 'Tampa mixes city energy with laid-back living. Discover someone who wants that same balance, and the same kind of connection.',
  },
  {
    name: 'Jacksonville Singles',
    text: 'Jacksonville gives you a wide local dating pool. Proximity is only the start; find someone whose values and everyday rhythm make the distance worth it.',
  },
];

const STEPS = [
  {
    title: 'Create Your Profile',
    text: 'Tell your story. Share who you are, what interests you, and what you’re looking for. The first step toward being understood, not just noticed.',
  },
  {
    title: 'Discover Local Connections',
    text: 'Find your kind of people. Explore Florida singles and look past the surface with tools like Love Diamond and Interests, which let you monitor and express interest in real time, so you get more clarity and less guessing about who’s actually interested.',
  },
  {
    title: 'Start a Conversation',
    text: 'Make it personal. Move beyond the profile, start talking, and find out what you have in common.',
  },
];

const SAFETY = [
  {
    title: 'Take Your Time Getting to Know Someone',
    text: 'You don’t have to tell your whole story on day one. Start with the conversation, share what feels right, and pay attention to how someone treats your boundaries.',
  },
  {
    title: 'Meet Safely in Person',
    text: 'When an online connection moves offline, keep the first meeting simple. Choose a public place, let someone know where you’ll be, and make your own way there and back.',
  },
  {
    title: 'Use Available Safety Tools',
    text: 'SLAMMMM’s available safety tools, including reporting, blocking, and privacy controls, help you stay in control. Use them when you need to, and keep the experience on your terms.',
  },
];

const FAQS = [
  {
    q: 'What is the best dating site in Florida?',
    a: "There isn’t one answer for everyone, and no single Florida dating website works the same way for every person. The better question is: does the platform help you find people who fit what you’re looking for? SLAMMMM focuses on compatibility, giving you more to consider than a photo or a quick swipe.",
  },
  {
    q: 'Is there a free dating site in Florida?',
    a: 'Yes. SLAMMMM lets you start for free. Create your profile, put yourself out there, and see who you connect with before deciding what comes next.',
  },
  {
    q: 'How can I meet single women in Florida?',
    a: "Start online, especially if your usual circle doesn’t introduce you to the kind of people you’re hoping to meet. With SLAMMMM, you can discover single women in Florida and look for the interests, values, and relationship goals you genuinely connect with.",
  },
  {
    q: 'Is online dating in Florida safe?',
    a: "It can be, when you keep your boundaries in the driver’s seat. Take conversations at your own pace, protect personal information, and meet somewhere public when you decide to meet in person. Use SLAMMMM’s available safety tools whenever you need them.",
  },
  {
    q: 'What are good first-date ideas in Florida?',
    a: 'Skip the pressure to plan something spectacular. A coffee, waterfront walk, casual dinner, local market, or shared activity gives you something to do without getting in the way of the conversation.',
  },
  {
    q: 'Where can I meet singles in Florida?',
    a: 'Start where you are. SLAMMMM helps you discover singles across Miami, Orlando, Tampa, Jacksonville, and other Florida communities, so you can explore connections close to home.',
  },
  {
    q: 'How much does online dating in Florida cost?',
    a: 'You can start with SLAMMMM for free, enough to create your profile and explore the platform before deciding what comes next.',
  },
];

const FAQ_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the best dating site in Florida?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "There isn’t one answer for everyone, and no single Florida dating website works the same way for every person. The better question is: does the platform help you find people who fit what you’re looking for? SLAMMMM focuses on compatibility, giving you more to consider than a photo or a quick swipe.",
      },
    },
    {
      '@type': 'Question',
      name: 'Is there a free dating site in Florida?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. SLAMMMM lets you start for free. Create your profile, put yourself out there, and see who you connect with before deciding what comes next.',
      },
    },
    {
      '@type': 'Question',
      name: 'How can I meet single women in Florida?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Start online, especially if your usual circle doesn’t introduce you to the kind of people you’re hoping to meet. With SLAMMMM, you can discover single women in Florida and look for the interests, values, and relationship goals you genuinely connect with.",
      },
    },
    {
      '@type': 'Question',
      name: 'Is online dating in Florida safe?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "It can be, when you keep your boundaries in the driver’s seat. Take conversations at your own pace, protect personal information, and meet somewhere public when you decide to meet in person. Use SLAMMMM’s available safety tools whenever you need them.",
      },
    },
    {
      '@type': 'Question',
      name: 'What are good first-date ideas in Florida?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Skip the pressure to plan something spectacular. A coffee, waterfront walk, casual dinner, local market, or shared activity gives you something to do without getting in the way of the conversation.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where can I meet singles in Florida?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Start where you are. SLAMMMM helps you discover singles across Miami, Orlando, Tampa, Jacksonville, and other Florida communities, so you can explore connections close to home.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does online dating in Florida cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can start with SLAMMMM for free, enough to create your profile and explore the platform before deciding what comes next.',
      },
    },
  ],
});

const SERVICE_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Online Dating in Florida',
  serviceType: 'Online Dating Service',
  description: 'SLAMMMM is an online dating platform for singles in Florida, helping people create profiles, explore potential matches, and connect with others based on compatibility, communication style, and relationship goals.',
  url: 'https://www.slammmm.com/florida',
  provider: {
    '@type': 'Organization',
    name: 'SLAMMMM',
    url: 'https://www.slammmm.com/',
  },
  areaServed: {
    '@type': 'State',
    name: 'Florida',
    containedInPlace: {
      '@type': 'Country',
      name: 'United States',
    },
  },
  audience: {
    '@type': 'Audience',
    audienceType: 'Singles',
  },
  serviceOutput: {
    '@type': 'Thing',
    name: 'Online dating connections',
  },
});

const Florida: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Seo
        title="Florida Dating Site | Meet Single Women & Men Near You"
        description="Join a top Florida dating site and meet single women & men near you. Free to browse, easy sign-up, start dating in Florida today."
        canonical="https://www.slammmm.com/florida"
        schemas={[FAQ_SCHEMA, SERVICE_SCHEMA]}
      />

      {/* Hero */}
      <section className="py-100 breadcrumb_section bg-light border-bottom">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto text-center">
              <div className="section_title">
                <span className="text-primary d-block font500 mb-2">Dating in Florida: Meet Local Singles</span>
                <h1 className="mb-4">Florida Dating That Goes Beyond First Impressions</h1>
              </div>
              <p className="font20 mb-3">
                SLAMMMM makes dating in Florida about more than who catches your eye first. Florida makes it easy to meet new people, but finding someone who actually fits your life is a different story. That&rsquo;s the gap a good dating site in Florida should close.
              </p>
              <p className="font20 mb-4">
                SLAMMMM looks past the surface. Our approach is built around values, lifestyle, communication style, and relationship goals. The things that actually bring two people closer, not just proximity.
              </p>
              <p className="font20 mb-4">
                Meet Florida singles with compatibility in mind. Getting started is simple and free.
              </p>
              <Link to="/signup"><button className="btn btn-primary">Join Free Today</button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro / value prop */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="row align-items-center">
              <div className="col-lg-5">
                <div className="section_title">
                  <h2 className="mb-0">Florida Dating Site Built for More Meaningful Connections</h2>
                </div>
              </div>
              <div className="col-lg-7">
                <p className="font20 mb-0">
                  A meaningful connection often starts with a single moment of curiosity. <Link to="/">SLAMMMM</Link> helps you discover connections based on what makes a relationship work, not just a first impression.
                </p>
              </div>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <div className="card h-100">
                <h3 className="font28 mb-3 pb-3 border-bottom">Free Dating Site in Florida</h3>
                <p className="mb-3 font20">
                  Start light, start free. Just you, your story, and a spark of curiosity. Build a profile that feels like you, then explore Florida singles without gates or guesswork.
                </p>
                <Link to="/signup"><button className="btn btn-outline-primary mt-2">Join Free Today</button></Link>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card h-100">
                <h3 className="font28 mb-3 pb-3 border-bottom">Look Beyond the Profile Picture</h3>
                <p className="mb-0 font20">
                  A photo draws you in. It won&rsquo;t tell you if you&rsquo;re compatible. Look past the picture and into the values, habits, and everyday things that actually shape a person, that&rsquo;s where real alignment starts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Single women / men */}
      <section className="py-100 our_values_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="row">
              <div className="col-lg-6">
                <div className="section_title">
                  <h2 className="mb-0">Meet Singles in Florida</h2>
                </div>
              </div>
              <div className="col-lg-6">
                <p className="font20 mb-0">
                  Location puts you in the same city. Compatibility gives you a reason to stay. Discover Florida singles who share your interests, values, and relationship goals, not just your zip code.
                </p>
              </div>
            </div>
          </div>

          <div className="d-flex flex-column gap-4">
            <div className="pb-4 border-bottom">
              <h3 className="font28 mb-3" style={{ fontSize: '2.2rem', lineHeight: 1.2, fontWeight: 700 }}>
                Single ladies in Florida near you
              </h3>
              <div className="d-flex gap-3 mb-3" aria-label="Women avatars" style={{ fontSize: '1.7rem', color: '#1c1c1c' }}>
                {[1, 2, 3].map((item) => (
                  <span
                    key={item}
                    className="d-inline-flex align-items-center justify-content-center rounded-circle border"
                    style={{ width: 42, height: 42, background: '#f5f5f5' }}
                  >
                    <i className="fa-solid fa-user" style={{ fontSize: '1.15rem' }}></i>
                  </span>
                ))}
              </div>
              <p className="mb-0 font20">
                Meet single women in Florida who bring their own interests, ambitions, and outlook to the table. Find common ground, start a conversation, and see where it goes.
              </p>
            </div>

            <div className="pb-4 border-bottom">
              <h3 className="font28 mb-3" style={{ fontSize: '2.2rem', lineHeight: 1.2, fontWeight: 700 }}>
                Single men in Florida
              </h3>
              <div className="d-flex gap-3 mb-3" aria-label="Men avatars" style={{ fontSize: '1.7rem', color: '#1c1c1c' }}>
                {[1, 2, 3].map((item) => (
                  <span
                    key={item}
                    className="d-inline-flex align-items-center justify-content-center rounded-circle border"
                    style={{ width: 42, height: 42, background: '#dfeaf8' }}
                  >
                    <i className="fa-solid fa-user" style={{ fontSize: '1.15rem' }}></i>
                  </span>
                ))}
              </div>
              <p className="mb-0 font20">
                Looking to meet single men in Florida? Go beyond the profile and discover what you actually have in common, from everyday interests to bigger relationship goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Dating Florida Cities Near You</h2>
            </div>
            <p className="font20 mb-0 mt-3">
              The right connection could be closer than you think. Explore singles across Florida&rsquo;s major cities and find people who fit your pace, interests, and idea of a meaningful relationship.
            </p>
          </div>

          <div className="row g-4">
            {CITIES.map((city) => (
              <div className="col-md-6 col-lg-3" key={city.name}>
                <div className="card h-100">
                  <h3 className="font22 mb-3 pb-2 border-bottom text-primary">{city.name}</h3>
                  <p className="mb-0 font20">{city.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-5">
            <Link to="/signup"><button className="btn btn-primary">Find Singles Near You</button></Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-100 our_approach_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title text-center">
              <h2 className="mb-0">How Our Florida Dating Site Works</h2>
            </div>
            <p className="font20 mb-0 mt-3 text-center">
              Online dating in Florida can be simple when you know what you&rsquo;re looking for. With SLAMMMM, it starts with you, moves toward compatibility, and leaves room for something real.
            </p>
          </div>

          <div className="row g-4">
            {STEPS.map((step, i) => (
              <div className="col-md-4" key={step.title}>
                <div className="card h-100">
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-3 pb-2">
                    <h3 className="font28 mb-0">{step.title}</h3>
                    <p className="font28 mb-0 text-primary opacity-50">{`0${i + 1}`}</p>
                  </div>
                  <p className="mb-0 font20">{step.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-5">
            <Link to="/signup"><button className="btn btn-primary">Start Dating in Florida</button></Link>
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title text-center">
              <h2 className="mb-0">Safe Online Dating in Florida</h2>
            </div>
            <p className="font20 mb-0 mt-3 text-center">
              Good dating starts with feeling comfortable enough to be yourself. Keep your pace, protect your space, and let trust earn its place.
            </p>
          </div>

          <div className="row g-4">
            {SAFETY.map((item) => (
              <div className="col-md-4" key={item.title}>
                <div className="card h-100">
                  <h3 className="font22 mb-3 pb-2 border-bottom">{item.title}</h3>
                  <p className="mb-0 font20">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-100 love_journey_section">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center z-3 position-relative">
              <div className="section_title mb-3">
                <h2 className="text-white">Start Your Florida Dating Journey</h2>
              </div>
              <p className="text-white font20 mb-4 pb-2">
                The Sunshine State has plenty of stories waiting to meet yours. Start with curiosity, find what aligns, and see where a connection can go.
              </p>
              <Link to="/signup"><button className="btn btn-primary">Sign Up Free, Meet Florida Singles</button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="section_title text-center mb-4">
            <h2 className="mb-0">Locations we serve</h2>
          </div>
          <div className="row g-3 justify-content-center">
            <div className="col-md-4">
              <Link to="/florida" className="btn btn-dark w-100">Florida</Link>
            </div>
            <div className="col-md-4">
              <Link to="/orlando" className="btn btn-dark w-100">Orlando, FL</Link>
            </div>
            <div className="col-md-4">
              <Link to="/new-york" className="btn btn-dark w-100">New York</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-100 help_faq_section" id="florida-faq">
        <div className="container">
          <div className="section_title text-center mb-5">
            <h2 className="mb-0">Florida Dating FAQ&rsquo;s</h2>
          </div>

          <div className="row">
            <div className="col-lg-9 mx-auto">
              <div className="accordion" id="florida-faq-accordion">
                {FAQS.map((item, index) => (
                  <div className="accordion-item" key={item.q}>
                    <h3 className="accordion-header">
                      <button
                        className={`accordion-button ${index === 0 ? '' : 'collapsed'}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#florida-faq-${index}`}
                        aria-expanded={index === 0 ? 'true' : 'false'}
                        aria-controls={`florida-faq-${index}`}
                      >
                        {item.q}
                      </button>
                    </h3>
                    <div
                      id={`florida-faq-${index}`}
                      className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                      data-bs-parent="#florida-faq-accordion"
                    >
                      <div className="accordion-body">{item.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Florida;
